import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Ban,
  Fingerprint,
  Globe,
  Globe2,
  KeyRound,
  Play,
  Plus,
  RefreshCw,
  ShieldCheck,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { Cargando } from '@/shared/ui/Cargando';
import { notificar } from '@/shared/ui/avisos';
import {
  CampoDelPanel,
  DosColumnas,
  PanelLateral,
  claseEntradaPanel,
} from '@/shared/ui/PanelLateral';
import { DialogoDeCredenciales } from './DialogoDeCredenciales';
import { ALCANCES, type Consumidor, type Credenciales, type TokenEmitido } from './tipos';

function loQueQueda(iso: string): string {
  const minutos = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
  const falta = Math.abs(minutos);

  const cuanto =
    falta < 60
      ? `${falta} min`
      : falta < 60 * 48
        ? `${Math.round(falta / 60)} h`
        : `${Math.round(falta / 1440)} días`;

  return minutos >= 0 ? `en ${cuanto}` : `hace ${cuanto}`;
}

function enFechaYHora(iso: string): string {
  const fecha = new Date(iso);
  return fecha.toLocaleString('es-EC', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ListaDeConsumidores() {
  const clienteDeConsultas = useQueryClient();
  // Ver la API es de quien supervisa; dar credenciales a un tercero, solo del admin.
  const puedeGestionar = useSesion().puede('api_publica.gestionar');

  const [creando, setCreando] = useState(false);
  const [credenciales, setCredenciales] = useState<Credenciales | null>(null);
  const [abierto, setAbierto] = useState<number | null>(null);

  const consumidores = useQuery({
    queryKey: ['api-publica', 'consumidores'],
    queryFn: () => api.get<Consumidor[]>('/api-publica/consumidores'),
  });

  function refrescar() {
    void clienteDeConsultas.invalidateQueries({ queryKey: ['api-publica'] });
  }

  const cambiarEstado = useMutation({
    mutationFn: ({ id, activar }: { id: number; activar: boolean }) =>
      api.post<void>(`/api-publica/consumidores/${id}/${activar ? 'activar' : 'desactivar'}`, {}),
    onSuccess: (_, { activar }) => {
      refrescar();
      notificar.exito(
        activar
          ? 'Institución reactivada'
          : 'Institución desactivada: sus tokens vivos dejaron de valer',
      );
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo cambiar el estado'),
  });

  const renovar = useMutation({
    mutationFn: (id: number) =>
      api.post<Credenciales>(`/api-publica/consumidores/${id}/renovar`, {}),
    onSuccess: (nuevas) => {
      refrescar();
      setCredenciales(nuevas);
    },
    onError: (causa) =>
      notificar.error(
        causa instanceof ErrorDeApi ? causa.message : 'No se pudieron renovar las credenciales',
      ),
  });

  if (consumidores.isLoading) {
    return <Cargando titulo="Cargando" detalle="quién puede llamar desde fuera" />;
  }

  const lista = consumidores.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs uppercase tracking-wide text-armazon-500">
          {lista.length === 0
            ? 'Todavía no hay nadie autorizado a llamar desde fuera'
            : plural(lista.filter((c) => c.activo).length, lista.length)}
        </p>

        {puedeGestionar && (
          <button
            type="button"
            onClick={() => setCreando(true)}
            title="Dar de alta un consumidor"
            className="grid size-10 shrink-0 place-items-center rounded-xl bg-marca-600 text-white transition hover:bg-marca-700"
          >
            <Plus className="size-5" aria-hidden />
            <span className="sr-only">Dar de alta una institución</span>
          </button>
        )}
      </div>

      {lista.length === 0 ? (
        <p className="rounded-2xl border border-armazon-200 bg-white p-12 text-center text-sm uppercase text-armazon-400">
          Ninguna institución puede llamar a la API todavía
        </p>
      ) : (
        <div className="space-y-3">
          {lista.map((c) => (
            <Ficha
              key={c.id}
              consumidor={c}
              abierto={abierto === c.id}
              alAbrir={() => setAbierto(abierto === c.id ? null : c.id)}
              alRenovar={() => renovar.mutate(c.id)}
              alCambiarEstado={() => cambiarEstado.mutate({ id: c.id, activar: !c.activo })}
              ocupado={renovar.isPending || cambiarEstado.isPending}
            />
          ))}
        </div>
      )}

      {creando && (
        <PanelDeAlta
          alCerrar={() => setCreando(false)}
          alCrear={(nuevas) => {
            setCreando(false);
            refrescar();
            setCredenciales(nuevas);
            notificar.exito(`INSTITUCIÓN «${nuevas.clave}» DADA DE ALTA.`);
          }}
        />
      )}

      {credenciales && (
        <DialogoDeCredenciales
          credenciales={credenciales}
          alCerrar={() => setCredenciales(null)}
        />
      )}
    </div>
  );
}

function plural(activas: number, total: number): string {
  return `${activas} ${activas === 1 ? 'activa' : 'activas'} de ${total}`;
}

function Ficha({
  consumidor,
  abierto,
  alAbrir,
  alRenovar,
  alCambiarEstado,
  ocupado,
}: {
  consumidor: Consumidor;
  abierto: boolean;
  alAbrir: () => void;
  alRenovar: () => void;
  alCambiarEstado: () => void;
  ocupado: boolean;
}) {
  const puedeGestionar = useSesion().puede('api_publica.gestionar');
  return (
    <article
      className={`rounded-2xl border bg-white shadow-sm transition ${
        consumidor.activo ? 'border-armazon-200' : 'border-armazon-100 opacity-70'
      }`}
    >
      <div className="flex flex-wrap items-start gap-4 p-4">
        <span
          className={`grid size-10 shrink-0 place-items-center rounded-xl ${
            consumidor.activo ? 'bg-marca-50 text-marca-600' : 'bg-armazon-100 text-armazon-400'
          }`}
        >
          <Globe className="size-5" aria-hidden />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-bold text-armazon-900">{consumidor.nombre}</h3>
            <code className="rounded-md bg-armazon-100 px-1.5 py-0.5 font-mono text-[11px] text-armazon-600">
              {consumidor.clave}
            </code>
            {!consumidor.activo && (
              <span className="rounded-full bg-armazon-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-armazon-500">
                Desactivado
              </span>
            )}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-armazon-500">
            <span className="inline-flex items-center gap-1">
              {consumidor.modo === 'oauth' ? (
                <KeyRound className="size-3.5" aria-hidden />
              ) : (
                <Fingerprint className="size-3.5" aria-hidden />
              )}
              {consumidor.modo === 'oauth' ? 'OAuth (v2)' : 'Llave fija (v1)'}
            </span>

            {consumidor.exigeFirma && (
              <span className="inline-flex items-center gap-1 text-marca-700">
                <ShieldCheck className="size-3.5" aria-hidden />
                Firma HMAC
              </span>
            )}

            <span>{consumidor.peticionesPorMinuto} peticiones/min</span>

            {consumidor.ipsAutorizadas.length > 0 ? (
              <span title={consumidor.ipsAutorizadas.join(', ')}>
                {consumidor.ipsAutorizadas.length} IP autorizada
                {consumidor.ipsAutorizadas.length === 1 ? '' : 's'}
              </span>
            ) : (
              <span className="text-amber-700">Desde cualquier IP</span>
            )}
          </div>

          <div className="mt-2 flex flex-wrap gap-1.5">
            {consumidor.alcances.map((a) => {
              const ficha = ALCANCES.find((x) => x.codigo === a);
              return (
                <span
                  key={a}
                  title={ficha?.detalle}
                  className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                    ficha?.delicado
                      ? 'bg-amber-100 text-amber-800'
                      : 'bg-armazon-100 text-armazon-600'
                  }`}
                >
                  {ficha?.rotulo ?? a}
                </span>
              );
            })}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          {puedeGestionar && (
            <>
              <Accion
                icono={RefreshCw}
                rotulo="Renovar credenciales"
                alPulsar={alRenovar}
                deshabilitado={ocupado}
              />
              <Accion
                icono={consumidor.activo ? Ban : Play}
                rotulo={consumidor.activo ? 'Desactivar' : 'Reactivar'}
                alPulsar={alCambiarEstado}
                deshabilitado={ocupado}
                peligroso={consumidor.activo}
              />
            </>
          )}
          <button
            type="button"
            onClick={alAbrir}
            className="rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-marca-700 transition hover:bg-marca-50"
          >
            {abierto ? 'Ocultar tokens' : 'Ver tokens'}
          </button>
        </div>
      </div>

      {abierto && <Tokens consumidorId={consumidor.id} />}
    </article>
  );
}

function Accion({
  icono: Icono,
  rotulo,
  alPulsar,
  deshabilitado,
  peligroso = false,
}: {
  icono: typeof RefreshCw;
  rotulo: string;
  alPulsar: () => void;
  deshabilitado: boolean;
  peligroso?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={alPulsar}
      disabled={deshabilitado}
      title={rotulo}
      className={`rounded-lg p-2 transition disabled:opacity-40 ${
        peligroso
          ? 'text-armazon-400 hover:bg-red-50 hover:text-red-600'
          : 'text-armazon-400 hover:bg-armazon-50 hover:text-armazon-700'
      }`}
    >
      <Icono className="size-4" aria-hidden />
      <span className="sr-only">{rotulo}</span>
    </button>
  );
}

function Tokens({ consumidorId }: { consumidorId: number }) {
  const clienteDeConsultas = useQueryClient();
  const puedeGestionar = useSesion().puede('api_publica.gestionar');

  const tokens = useQuery({
    queryKey: ['api-publica', 'tokens', consumidorId],
    queryFn: () => api.get<TokenEmitido[]>(`/api-publica/consumidores/${consumidorId}/tokens`),
  });

  const revocar = useMutation({
    mutationFn: (tokenId: number) =>
      api.post<void>(
        `/api-publica/consumidores/${consumidorId}/tokens/${tokenId}/revocar`,
        {},
      ),
    onSuccess: () => {
      void clienteDeConsultas.invalidateQueries({ queryKey: ['api-publica', 'tokens'] });
      notificar.exito('TOKEN REVOCADO. EL SIGUIENTE QUE PIDA SALDRÁ BUENO.');
    },
    onError: (causa) =>
      notificar.error(causa instanceof ErrorDeApi ? causa.message : 'No se pudo revocar el token'),
  });

  const lista = tokens.data ?? [];

  return (
    <div className="border-t border-armazon-100 bg-armazon-50/40 px-4 py-3">
      {tokens.isPending ? (
        <p className="text-[11px] uppercase tracking-wide text-armazon-400">
          Buscando sus tokens…
        </p>
      ) : lista.length === 0 ? (
        <p className="text-[11px] uppercase tracking-wide text-armazon-400">
          Todavía no ha pedido ningún token de acceso.
        </p>
      ) : (
        <ul className="divide-y divide-armazon-100">
          {lista.map((t) => (
            <li key={t.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2.5">
              <code className="font-mono text-[11px] text-armazon-700">{t.prefijo}</code>

              <EstadoDelToken token={t} />

              <span className="text-[11px] text-armazon-500" title={enFechaYHora(t.creadoEn)}>
                Pedido {loQueQueda(t.creadoEn)}
              </span>
              <span className="text-[11px] text-armazon-400" title={enFechaYHora(t.expiraEn)}>
                {t.vivo ? 'Caduca' : 'Caducó'} {loQueQueda(t.expiraEn)}
              </span>

              {t.ipOrigen && (
                <span className="inline-flex items-center gap-1 text-[11px] text-armazon-400">
                  <Globe2 className="size-3" aria-hidden />
                  {t.ipOrigen}
                </span>
              )}

              {t.alcances.length > 0 && (
                <span className="flex flex-wrap gap-1">
                  {t.alcances.map((a) => {
                    const ficha = ALCANCES.find((x) => x.codigo === a);
                    return (
                      <span
                        key={a}
                        className="rounded-full bg-armazon-100 px-1.5 py-0.5 text-[10px] font-semibold text-armazon-600"
                      >
                        {ficha?.rotulo ?? a}
                      </span>
                    );
                  })}
                </span>
              )}

              {t.vivo && puedeGestionar && (
                <button
                  type="button"
                  onClick={() => revocar.mutate(t.id)}
                  disabled={revocar.isPending}
                  className="ml-auto inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-red-600 transition hover:bg-red-50 disabled:opacity-40"
                >
                  <Ban className="size-3" aria-hidden />
                  Revocar
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EstadoDelToken({ token }: { token: TokenEmitido }) {
  const estado = token.revocadoEn
    ? { rotulo: 'Revocado', clase: 'bg-red-100 text-red-700' }
    : token.vivo
      ? { rotulo: 'Vivo', clase: 'bg-marca-100 text-marca-700' }
      : { rotulo: 'Caducado', clase: 'bg-armazon-100 text-armazon-500' };

  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${estado.clase}`}
    >
      {estado.rotulo}
    </span>
  );
}

function PanelDeAlta({
  alCerrar,
  alCrear,
}: {
  alCerrar: () => void;
  alCrear: (credenciales: Credenciales) => void;
}) {
  const [nombre, setNombre] = useState('');
  const [correo, setCorreo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [modo, setModo] = useState<'oauth' | 'llave'>('oauth');
  const [alcances, setAlcances] = useState<string[]>(['consulta:deuda']);
  const [exigeFirma, setExigeFirma] = useState(true);
  const [ips, setIps] = useState('');
  const [limite, setLimite] = useState(60);
  const [minutos, setMinutos] = useState(60);
  const [error, setError] = useState<string | null>(null);

  const crear = useMutation({
    mutationFn: () =>
      api.post<Credenciales>('/api-publica/consumidores', {
        nombre,
        modo,
        descripcion: descripcion || null,
        contactoCorreo: correo || null,
        alcances,
        ipsAutorizadas: ips
          .split(/[\s,]+/)
          .map((s) => s.trim())
          .filter(Boolean),
        exigeFirma,
        peticionesPorMinuto: limite,
        minutosDeToken: minutos,
      }),
    onSuccess: alCrear,
    onError: (causa) => {
      const mensaje =
        causa instanceof ErrorDeApi ? causa.message : 'No se pudo dar de alta la institución';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  function alternar(codigo: string) {
    setAlcances((previos) =>
      previos.includes(codigo) ? previos.filter((a) => a !== codigo) : [...previos, codigo],
    );
  }

  return (
    <PanelLateral
      abierto
      alCerrar={alCerrar}
      titulo="Dar de alta una institución"
      descripcion="Le entrega una llave para llamar desde fuera"
      icono={Globe}
      alEnviar={(evento) => {
        evento.preventDefault();
        crear.mutate();
      }}
      enviando={crear.isPending}
    >
      {error && (
        <p className="rounded-xl bg-red-50 p-3 text-xs text-red-700">{error}</p>
      )}

      <CampoDelPanel
        etiqueta="Nombre"
        requerido
        ayuda="De aquí sale la «key» que va en el manual del integrador"
      >
        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder="Cooperativa San Francisco"
          className={claseEntradaPanel}
        />
      </CampoDelPanel>

      <DosColumnas>
        <CampoDelPanel etiqueta="Correo de contacto">
          <input
            type="email"
            value={correo}
            onChange={(e) => setCorreo(e.target.value)}
            placeholder="sistemas@cooperativa.fin.ec"
            className={claseEntradaPanel}
          />
        </CampoDelPanel>

        <CampoDelPanel
          etiqueta="Cómo se identifica"
          ayuda="Un alta nueva va en OAuth: la llave fija es para lo que ya estaba integrado"
        >

          <select
            value={modo}
            onChange={(e) => setModo(e.target.value as 'oauth' | 'llave')}
            className={claseEntradaPanel}
          >
            <option value="oauth">
              OAUTH 2.0 — PIDE UN TOKEN QUE CADUCA, CON CLIENT_ID Y SECRET
            </option>
            <option value="llave">
              LLAVE FIJA EN LA CABECERA — NO CADUCA, PARA INTEGRACIONES ANTIGUAS
            </option>
          </select>
        </CampoDelPanel>
      </DosColumnas>

      <CampoDelPanel etiqueta="Descripción">
        <input
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          placeholder="Recaudación en ventanillas de la cooperativa"
          className={claseEntradaPanel}
        />
      </CampoDelPanel>

      <CampoDelPanel etiqueta="Qué le deja hacer" requerido>
        <div className="space-y-2">
          {ALCANCES.map((a) => (
            <label
              key={a.codigo}
              className={`flex cursor-pointer items-start gap-2.5 rounded-xl border p-3 transition ${
                alcances.includes(a.codigo)
                  ? a.delicado
                    ? 'border-amber-300 bg-amber-50/60'
                    : 'border-marca-300 bg-marca-50/60'
                  : 'border-armazon-200 hover:border-armazon-300'
              }`}
            >
              <input
                type="checkbox"
                checked={alcances.includes(a.codigo)}
                onChange={() => alternar(a.codigo)}
                className="mt-0.5 size-4 rounded border-armazon-300"
              />
              <span className="min-w-0">
                <span className="block text-xs font-semibold uppercase text-armazon-800">
                  {a.rotulo}
                </span>
                <span className="block text-[11px] uppercase text-armazon-500">{a.detalle}</span>
              </span>
            </label>
          ))}
        </div>
      </CampoDelPanel>

      <CampoDelPanel
        etiqueta="IP autorizadas"
        ayuda="Vacío deja entrar desde cualquier sitio. Separe con comas o espacios"
      >
        <input
          value={ips}
          onChange={(e) => setIps(e.target.value)}
          placeholder="200.1.2.3, 200.1.2.4"
          className={claseEntradaPanel}
        />
      </CampoDelPanel>

      <DosColumnas>
        <CampoDelPanel etiqueta="Peticiones por minuto">
          <input
            type="number"
            min={1}
            max={6000}
            value={limite}
            onChange={(e) => setLimite(Number(e.target.value))}
            className={claseEntradaPanel}
          />
        </CampoDelPanel>

        <CampoDelPanel etiqueta="Minutos que dura el token" ayuda="Entre 5 y 720">
          <input
            type="number"
            min={5}
            max={720}
            value={minutos}
            disabled={modo === 'llave'}
            onChange={(e) => setMinutos(Number(e.target.value))}
            className={`${claseEntradaPanel} disabled:bg-armazon-50 disabled:text-armazon-400`}
          />
        </CampoDelPanel>
      </DosColumnas>

      <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-armazon-200 p-3">
        <input
          type="checkbox"
          checked={exigeFirma}
          onChange={(e) => setExigeFirma(e.target.checked)}
          className="mt-0.5 size-4 rounded border-armazon-300"
        />
        <span>
          <span className="block text-xs font-semibold uppercase text-armazon-800">
            Exigir firma HMAC en cada petición
          </span>
          <span className="block text-[11px] uppercase text-armazon-500">
            Sin ella, la «key» que va en el manual basta para entrar. Quítela sólo para el
            entorno de pruebas del integrador.
          </span>
        </span>
      </label>
    </PanelLateral>
  );
}
